import flagrClient from '@/infrastructure/flagr'
import { useAppSelector } from '@/redux/hooks'
import { useEffect, useState, useSyncExternalStore } from 'react'

// Pages remount on every navigation, so answers outlive the hook: a tab switch reads them in its
// first render instead of flashing its loading state and asking Flagr again.
const answers = new Map<string, boolean>()
const inFlight = new Map<string, Promise<boolean | undefined>>()
const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function resetFeatureFlagCache() {
  answers.clear()
  inFlight.clear()
}

// Resolves undefined when the evaluation fails: it is not cached, so the next mount asks again.
function evaluate(
  key: string,
  flagID: number,
  context: Record<string, unknown>
): Promise<boolean | undefined> {
  const pending = inFlight.get(key)
  if (pending) return pending
  const request = flagrClient
    .callFlagrFlag(flagID, context)
    .then((enabled) => {
      answers.set(key, enabled)
      listeners.forEach((listener) => listener())
      return enabled
    })
    .catch(() => undefined)
    .finally(() => inFlight.delete(key))
  inFlight.set(key, request)
  return request
}

export function useFeatureFlag(
  flagID: number,
  additionalContext: Record<string, unknown> = {},
  { enabled = true }: { enabled?: boolean } = {}
) {
  const settings = useAppSelector((s) => s.settingsApi.settings.response)
  // Callers pass an object literal (or none, taking the default), so the reference is new on
  // every render. Depending on it re-evaluated the flag on each render — 11 Flagr calls per
  // page load — so the effect keys off the contents instead.
  const contextKey = JSON.stringify(additionalContext)
  const evaluationKey = JSON.stringify([
    flagID,
    settings.group,
    settings.ecommerce_id,
    contextKey
  ])
  // Subscribed, so an answer another hook's call caches before this one's effect still renders.
  const cached = useSyncExternalStore(subscribe, () =>
    answers.get(evaluationKey)
  )
  // Keyed by context so a new context reads as loading in the same render, not one effect later.
  const [failedKey, setFailedKey] = useState<string>()

  useEffect(() => {
    if (!enabled || answers.has(evaluationKey)) return
    // A slower answer for the previous context must not overwrite the current one.
    let current = true
    evaluate(evaluationKey, flagID, {
      // Every spelling Flagr constraints use, as the backend's flags.NewEntity sends.
      group: settings.group,
      ecommerce_id: settings.ecommerce_id,
      ecommerceid: settings.ecommerce_id,
      ecommerceId: settings.ecommerce_id,
      ...JSON.parse(contextKey)
    }).then((enabled) => {
      if (current && enabled === undefined) setFailedKey(evaluationKey)
    })
    return () => {
      current = false
    }
  }, [
    enabled,
    flagID,
    settings.group,
    settings.ecommerce_id,
    contextKey,
    evaluationKey
  ])

  // Off-until-proven-on: gates that must not flash their negative state (a 404, an empty page)
  // wait on this instead of trusting the previous context's answer.
  // A disabled flag asks nobody and reads as off.
  const known = !enabled
    ? false
    : (cached ?? (failedKey === evaluationKey ? false : undefined))
  const isFlagLoading = known === undefined
  const isFlagEnabled = !!known
  return { isFlagEnabled, isFlagLoading }
}
